import React, { useMemo } from 'react';
import classNames from 'classnames';
import useT from '@/lib/t';
import { isEmptyString, isNotSet, isSet } from '@/utils/formatChecker';

import ConditionalFragment from '@/components/common/ConditionalFragment';
import { IListingInBuyPage } from './HouseDetailContainerProps';
import { getParkingDetail, totalSizeFormatter } from '@/utils/dataConvertor';
import { areaFormatter, itemHasOrNotFormatter, lotSizeTitleLangKeyFormatter } from '@/src/libs/number-helper';
import { amenitiesFormatter } from '@/src/libs/special-helper';
import { CountryEnum } from '@/swagger';

const MainData = ({ detailData }: { detailData: IListingInBuyPage }) => {
  const t = useT('listing');

  const parkingSize = Number.isNaN(parseFloat(detailData.detail_parking_size ?? ''))
    ? undefined
    : detailData.detail_parking_size;

  const parkingDetail = useMemo(() => getParkingDetail(detailData).join('、'), [detailData]);

  return (
    <div className={'py-8 border-b border-gray300'}>
      <section id='house-detail' className={classNames('text-lg font-medium text-dark-grey uppercase my-3')}>
        詳細資料
      </section>
      <div>
        <MainDataItemDescription
          name={'建設公司'}
          value={detailData.listing_advanced_info?.builder_project_name}
          hide={
            !detailData.listing_advanced_info?.builder_project_name ||
            !detailData.listing_advanced_info.display_builder_project_name
          }
        />
        <MainDataItemDescription
          name={'建案類型'}
          value={detailData.detail_category}
          hide={!detailData.detail_category}
        />
        <MainDataItemDescription
          name={'登記用途'}
          value={detailData.detail_purpose}
          hide={!detailData.detail_purpose}
        />
        <MainDataItemDescription
          name={'總面積'}
          value={totalSizeFormatter(detailData.detail_total_area_size, detailData.country!, 'inline')}
        />
        <MainDataItemDescription
          name={'房屋權狀坪數'}
          value={`${totalSizeFormatter(
            areaFormatter(detailData['detail_total_area_size'], {
              omit: [parkingSize!],
              country: detailData.country!,
            }),
            detailData.country!,
            'inline'
          )}`}
          hide={
            isNotSet(detailData.detail_main_area_size) &&
            isNotSet(detailData.detail_other_area_size) &&
            isNotSet(detailData.detail_amenities_area) &&
            isNotSet(parkingSize)
          }
          isSubItem
        />
        <MainDataItemDescription
          name={`主建物`}
          value={`${totalSizeFormatter(detailData['detail_main_area_size'], detailData.country!, 'inline')}`}
          isSubItem
          isSubText
          hide={isNotSet(detailData['detail_main_area_size'])}
        />
        <MainDataItemDescription
          name={`附屬建物`}
          value={`${totalSizeFormatter(detailData['detail_other_area_size'], detailData.country!, 'inline')}`}
          isSubItem
          isSubText
          hide={isNotSet(detailData['detail_other_area_size'])}
        />
        <MainDataItemDescription
          name={`公共設施${amenitiesFormatter(detailData.is_parking_space_in_amenities)}`}
          value={`${totalSizeFormatter(detailData['detail_amenities_area'], detailData.country!, 'inline')}`}
          isSubItem
          isSubText
          hide={isNotSet(detailData['detail_amenities_area'])}
        />
        <MainDataItemDescription
          name={`車位`}
          value={`${totalSizeFormatter(parkingSize!, detailData.country!, 'inline')}`}
          isSubItem
          isSubText
          hide={!(parkingSize && !detailData.is_parking_space_in_amenities)}
        />
        <MainDataItemDescription
          name={t(lotSizeTitleLangKeyFormatter(detailData.detail_category))}
          value={totalSizeFormatter(detailData.detail_lot_size, detailData.country!, 'inline')}
          hide={!detailData.detail_lot_size}
        />
        <MainDataItemDescription
          name={'警衛管理'}
          value={`${itemHasOrNotFormatter(detailData['detail_guard_management'])}`}
        />
        <MainDataItemDescription
          name={'管理費'}
          value={detailData.detail_management_fee}
          hide={isEmptyString(detailData.detail_management_fee ?? '')}
        />
        <MainDataItemDescription name={'車位類型'} value={parkingDetail} hide={isEmptyString(parkingDetail ?? '')} />
        <MainDataItemDescription name={'公共設施'} value={detailData.detail_amenities} />
        <MainDataItemDescription
          name={'公設比'}
          value={`${detailData.listing_advanced_info?.public_usage_ratio}%`}
          hide={!detailData.listing_advanced_info?.public_usage_ratio || detailData.country !== CountryEnum.TW}
        />
      </div>
      <ConditionalFragment condition={detailData.country === 'US'}>
        <p className={classNames('text-sm text-gray500 py-2')}>
          1 坪 = 35.5832 平方英呎。美國物件原面積單位為「平方英呎」，轉換成「坪」會有些許落差。
        </p>
      </ConditionalFragment>
    </div>
  );
};

const MainDataItemDescription = ({
  name,
  value,
  isSubItem,
  isSubText,
  unit,
  alignments = 'center',
  endWithBorder,
  hide,
}: {
  name: string;
  value?: string | null | number;
  isSubItem?: boolean;
  isSubText?: boolean;
  unit?: string;
  alignments?: 'center' | 'start';
  endWithBorder?: boolean;
  hide?: boolean;
}) => {
  if (hide) return null;

  return (
    <div
      className={classNames(
        `w-full pr-4`,
        {
          'bg-gray50 pl-4': isSubItem,
          'bg-white': !isSubItem,
        },
        {
          'items-center': alignments === 'center',
          'items-start': alignments === 'start',
        }
      )}
    >
      <div
        className={classNames('flex flex-row flex-nowrap justify-between', 'py-2 ', {
          'border-b border-solid': endWithBorder,
        })}
      >
        <p
          className={classNames(
            'flex-shrink-0',
            { 'w-1/2 max-w-[7.5rem]': !isSubItem, 'w-auto': isSubItem },
            'leading-normal tracking-wide uppercase',
            {
              'text-gray500': isSubText,
              'text-gray900': !isSubText,
            }
          )}
        >
          {name}
        </p>
        <ConditionalFragment condition={isSet(value)}>
          <div
            className={classNames(
              'min-w-1/2 flex-1 flex-shrink-0',
              'leading-normal tracking-wide uppercase text-right'
            )}
          >
            {`${value} ${unit ? unit : ''}`.split('\n').map((p) => (
              <p
                key={p}
                className={classNames({
                  'text-gray500': isSubText,
                  'text-gray900': !isSubText,
                })}
              >
                {p}
              </p>
            ))}
          </div>
        </ConditionalFragment>
      </div>
    </div>
  );
};

// const CollapsibleData: React.FC<{
//   title: string;
//   titleValue: string | null | number;
//   titleUnit: string;
// }> = ({ title, titleValue, titleUnit, children }) => {
//   const [isExpanded, setIsExpanded] = useState<boolean>(false);
//   const collapsibleRef = useRef<HTMLDivElement>(null);
//
//   const displayText = useMemo(() => {
//     if (isExpanded) return `隱藏細節`;
//     else return `查看細節`;
//   }, [isExpanded, title]);
//
//   const height = useMemo(() => {
//     if (isExpanded) return collapsibleRef.current?.scrollHeight || 0;
//     else return 0;
//   }, [isExpanded]);
//
//   return (
//     <div>
//       <div
//         className={classNames(
//           'flex flex-row flex-nowrap',
//           'pl-xl py-2 pr-4',
//           'w-full',
//           'bg-gray50',
//           'text-gray900',
//           'cursor-pointer'
//         )}
//         onClick={() => setIsExpanded((prev) => !prev)}
//       >
//         <div className={'flex items-center mr-2'}>
//           <span className={'flex-1 truncate'}>{title}</span> (<p className={'underline'}>{displayText}</p>)
//         </div>
//         <div className={'ml-auto'}>
//           {titleValue}
//           {titleUnit}
//         </div>
//       </div>
//       <div
//         ref={collapsibleRef}
//         style={{ height, transition: 'height', transitionDuration: '300ms' }}
//         className={classNames('overflow-hidden')}
//       >
//         {children}
//       </div>
//     </div>
//   );
// };

export default MainData;
